import cx from "classnames";
import type { MouseEvent, ReactNode } from "react";
import { Link } from "react-router";

import S from "./RouteTabs.module.css";

export type RouteTab<Value extends string = string> = {
  readonly label: ReactNode;
  readonly value: Value;
  readonly to: string;
  readonly leftSection?: ReactNode;
  readonly rightSection?: ReactNode;
};

type RouteTabsProps<Value extends string> = {
  "aria-label": string;
  tabs: readonly RouteTab<Value>[];
  value: Value | null;
  variant?: "default" | "pills";
  onChange?: (value: Value) => void;
};

function shouldHandleNavigation(event: MouseEvent<HTMLAnchorElement>) {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

export function RouteTabs<Value extends string>({
  "aria-label": ariaLabel,
  tabs,
  value,
  variant = "default",
  onChange,
}: RouteTabsProps<Value>) {
  const handleClick = (
    event: MouseEvent<HTMLAnchorElement>,
    nextValue: Value,
  ) => {
    if (onChange != null && shouldHandleNavigation(event)) {
      event.preventDefault();
      onChange(nextValue);
    }
  };

  return (
    <nav
      aria-label={ariaLabel}
      className={cx(S.list, { [S.pills]: variant === "pills" })}
    >
      {tabs.map((tab) => {
        const isActive = tab.value === value;

        return (
          <Link
            key={tab.value}
            to={tab.to}
            className={S.tab}
            aria-current={isActive ? "page" : undefined}
            style={{
              color: isActive ? "var(--mb-color-text-primary)" : undefined,
            }}
            onClick={(event) => handleClick(event, tab.value)}
          >
            {tab.leftSection != null && (
              <span className={cx(S.section, S.leftSection)}>
                {tab.leftSection}
              </span>
            )}
            <span className={S.label}>{tab.label}</span>
            {tab.rightSection != null && (
              <span className={cx(S.section, S.rightSection)}>
                {tab.rightSection}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}
